import React from 'react';
import { motion } from 'framer-motion';
import { Apple, Play, Smartphone, Zap } from 'lucide-react';
import { fadeInUp } from '../../lib/animations';
import Button from '../ui/Button';

interface ToolCtaProps {
  /** Slug da ferramenta — usado nos parâmetros UTM dos links de download */
  toolSlug: string;
}

/**
 * CTA de download ao final de cada ferramenta/calculadora.
 * Links incluem UTM específico por ferramenta para rastreamento granular.
 */
const ToolCta: React.FC<ToolCtaProps> = ({ toolSlug }) => {
  const utmSuffix = `utm_source=ferramentas&utm_medium=cta&utm_campaign=${encodeURIComponent(toolSlug)}`;
  const androidUrl = `https://play.google.com/store/apps/details?id=info.marcussoftware.gas2&hl=pt_BR&${utmSuffix}`;
  const iosUrl = `https://apps.apple.com/br/app/gas2-controle-de-combust%C3%ADvel/id6755345109?${utmSuffix}`;

  return (
    <motion.section
      variants={fadeInUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      className="mt-10"
    >
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-amber-400 via-amber-400 to-orange-500 p-8 sm:p-12 text-center shadow-amber">
        {/* Brilhos decorativos */}
        <div className="pointer-events-none absolute top-0 right-0 h-56 w-56 rounded-full bg-orange-300/30 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 left-0 h-40 w-40 rounded-full bg-yellow-300/30 blur-3xl" />

        <div className="relative z-10">
          {/* Ícone */}
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-900/20 backdrop-blur-sm">
            <Smartphone className="h-7 w-7 text-gray-900" strokeWidth={2} />
          </div>

          {/* Badge */}
          <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-gray-900/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gray-900">
            <Zap className="h-3.5 w-3.5" strokeWidth={2} />
            Gratuito · Android e iOS
          </span>

          {/* Título */}
          <h2 className="font-display mt-2 text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">
            Automatize esse controle com o Gas2
          </h2>

          {/* Descrição */}
          <p className="mx-auto mt-4 max-w-md text-sm sm:text-[15px] text-gray-800 leading-relaxed">
            Registre abastecimentos, monitore o consumo real, compare álcool vs gasolina e
            acompanhe manutenções — tudo em um só lugar.{' '}
            <strong className="text-gray-900">Baixe grátis agora!</strong>
          </p>

          {/* Botões de download */}
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button href={androidUrl} variant="dark" size="lg" icon={Play} target="_blank">
              Google Play
            </Button>
            <Button
              href={iosUrl}
              variant="outline"
              size="lg"
              icon={Apple}
              target="_blank"
              className="!border-gray-900/20 !bg-white/30 !text-gray-900 hover:!bg-white hover:!text-gray-900"
            >
              App Store
            </Button>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default ToolCta;

